import { ArrowLeft, FileText, GitBranch, Pencil } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader } from "@/components/common/loader";
import { openDocument } from "@/features/documents/api";
import { updateNotice } from "@/features/notices/api";
import { NoticeFormDialog } from "@/features/notices/components/notice-form-dialog";
import { NoticeStepsPanel } from "@/features/notices/components/notice-steps-panel";
import { OpenCaseDialog } from "@/features/notices/components/open-case-dialog";
import { useNotice } from "@/features/notices/hooks/use-notice";
import { NOTICE_STATUS_OPTIONS, type NoticeStatus } from "@/features/notices/types";
import { useActiveClient } from "@/layouts/client-layout";

const severityClass: Record<string, string> = {
  URGENT: "border-destructive/20 bg-destructive/10 text-destructive",
  ACTION_REQUIRED: "border-warning/20 bg-warning/10 text-warning",
  INFO: "border-muted-foreground/20 bg-muted text-muted-foreground",
};

export default function ClientNoticeDetailPage() {
  const { id: clientId, noticeId } = useParams<{ id: string; noticeId: string }>();
  const { client } = useActiveClient();
  const navigate = useNavigate();
  const { notice, loading, error, refetch } = useNotice(noticeId!);
  const [editOpen, setEditOpen] = useState(false);
  const [openCaseOpen, setOpenCaseOpen] = useState(false);
  const [resolvePrompt, setResolvePrompt] = useState(false);

  const noticesHref = `/client/${clientId}/irs-notices`;

  async function changeStatus(status: NoticeStatus) {
    if (!notice) return;
    try {
      await updateNotice(notice.id, {
        notice_definition: notice.noticeDefinition,
        received_on: notice.receivedOn,
        status,
      });
      refetch();
    } catch {
      toast.error("Couldn't update status.");
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader />
      </div>
    );
  }

  if (error || !notice) {
    return (
      <div className="flex flex-col gap-4">
        <Link to={noticesHref} className="text-primary text-sm hover:underline">
          <ArrowLeft className="mr-1 inline size-4" /> Back to notices
        </Link>
        <p className="text-destructive text-sm">{error ?? "Notice not found."}</p>
      </div>
    );
  }

  const deadlineLabel =
    notice.daysUntilDeadline === null
      ? null
      : notice.daysUntilDeadline < 0
        ? `Overdue by ${Math.abs(notice.daysUntilDeadline)} days`
        : `Due in ${notice.daysUntilDeadline} days`;

  return (
    <div className="flex flex-col gap-6">
      <Link to={noticesHref} className="text-primary text-sm hover:underline">
        <ArrowLeft className="mr-1 inline size-4" /> Back to notices
      </Link>

      {/* Header */}
      <Card className="gap-4 p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-foreground text-2xl font-bold">
                {notice.noticeCode} — {notice.noticeTitle}
              </h1>
              <Badge
                variant="outline"
                className={severityClass[notice.severity] ?? severityClass.INFO}
              >
                {notice.severity}
              </Badge>
              <Badge variant="outline">{notice.category}</Badge>
            </div>
            <p className="text-muted-foreground max-w-2xl text-sm">
              {notice.recommendedAction}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="size-4" /> Edit
            </Button>
            <Button
              size="sm"
              disabled={notice.case !== null}
              onClick={() => setOpenCaseOpen(true)}
            >
              <GitBranch className="size-4" />
              {notice.case ? "Case opened" : "Open Case"}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Meta label="Received">{notice.receivedOn}</Meta>
          <Meta label="Deadline">
            {notice.responseDeadline ?? "—"}
            {deadlineLabel && (
              <span className="text-muted-foreground ml-1 text-xs">({deadlineLabel})</span>
            )}
          </Meta>
          <div className="flex flex-col gap-1">
            <span className="text-muted-foreground text-xs font-semibold">Status</span>
            <Select value={notice.status} onValueChange={(v) => changeStatus(v as NoticeStatus)}>
              <SelectTrigger className="h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {NOTICE_STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Meta label="Tax Year">{notice.taxYear || "—"}</Meta>
        </div>

        {notice.document && (
          <button
            type="button"
            onClick={() =>
              openDocument(notice.document!).catch(() =>
                toast.error("Couldn't open the document."),
              )
            }
            className="border-border bg-muted/50 hover:bg-muted flex items-center gap-3 rounded-lg border p-3 text-left"
          >
            <FileText className="text-muted-foreground size-5" />
            <span className="text-foreground text-sm font-medium">View scanned notice</span>
          </button>
        )}
      </Card>

      {/* Action steps */}
      <Card className="gap-4 p-6">
        <h2 className="text-foreground text-lg font-semibold">Action Steps</h2>
        <NoticeStepsPanel
          notice={notice}
          onChanged={refetch}
          onAllStepsComplete={() => setResolvePrompt(true)}
        />
      </Card>

      <NoticeFormDialog
        clientId={client.id}
        notice={notice}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={refetch}
      />

      <OpenCaseDialog
        notice={notice}
        open={openCaseOpen}
        onOpenChange={setOpenCaseOpen}
        onOpened={(caseId) => navigate(`/client/${clientId}/cases/${caseId}`)}
      />

      <AlertDialog open={resolvePrompt} onOpenChange={setResolvePrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Resolve this notice?</AlertDialogTitle>
            <AlertDialogDescription>
              All action steps are complete. Mark the notice as Resolved?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setResolvePrompt(false);
                void changeStatus("RESOLVED");
              }}
            >
              Resolve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs font-semibold">{label}</span>
      <span className="text-foreground text-sm">{children}</span>
    </div>
  );
}
